/**
 * `world_clock` — `WorldClockValue` (`specs/M2-tier2-column-types.md` §5.6, `[09§A.7 world_clock]`).
 *
 * `value` is `{"timezone":"Europe/Rome","changed_at":…}` — an IANA `Continent/City` identifier.
 *
 * **`text` is the identifier with underscores turned into spaces**, not a rendered time and not
 * the raw id: `America/New_York` renders as `"America/New York"` (measured, 2026-09-10 snapshot,
 * 126 populated cells). The clock itself is drawn by the renderer from the viewer's `now`.
 */
import { z } from 'zod';
import { defaultFilterSql, defaultSummarySql } from '../sql.js';
import { textShadow, toFragmentTimestamp } from '../helpers.js';
import { ColumnValueError, defineColumnType } from '../types.js';
export const worldClockValueSchema = z.looseObject({
    timezone: z.string(),
    changed_at: z.string().nullable().optional(),
});
import { worldClockFilterConditions } from './filters.js';
import { operatorsOf } from '../filter-conditions.js';
export const worldClockSettingsSchema = z.looseObject({
    /** `"HH:mm"` | `"hh:mm A"` `[09§A.7 world_clock]`. */
    format: z.string().optional(),
    /** String `"0"`–`"23"` in monday's own settings. */
    startWorkingHours: z.string().optional(),
    endWorkingHours: z.string().optional(),
    show_utc_offset: z.boolean().optional(),
    hide_footer: z.boolean().optional(),
});
const ZONE_RE = /^[A-Za-z][A-Za-z0-9+_-]*(?:\/[A-Za-z0-9+_-]+)*$/;
function normalize(raw, keepVolatile) {
    if (raw === null || raw === undefined)
        return null;
    const zone = typeof raw === 'string' ? raw : raw.timezone;
    if (zone === null || zone === undefined || zone === '')
        return null;
    if (typeof zone !== 'string')
        throw new ColumnValueError('world_clock', '"timezone" must be a string');
    if (!ZONE_RE.test(zone)) {
        throw new ColumnValueError('world_clock', `"${zone}" is not an IANA timezone identifier`);
    }
    const value = { timezone: zone };
    if (keepVolatile && typeof raw === 'object') {
        const source = raw;
        if ('changed_at' in source)
            value.changed_at = source.changed_at;
    }
    return value;
}
/** `America/New_York` → `America/New York` (measured). */
export function worldClockText(zone) {
    return zone.replace(/_/g, ' ');
}
export const worldClockColumn = defineColumnType({
    type: 'world_clock',
    title: 'World Clock',
    description: 'A timezone, shown as the local time there.',
    apiFragmentName: 'WorldClockValue',
    legacyTypeName: 'timezone',
    idPrefix: 'world_clock',
    valueSchema: worldClockValueSchema,
    settingsSchema: worldClockSettingsSchema,
    defaultSettings: {},
    settingsStrSchema: worldClockSettingsSchema,
    writable: true,
    /**
     * Verbatim: *"column type TimezoneColumn is not supporting changing the column value with
     * simple column value."* `[09§A.7 world_clock]`.
     */
    supportsSimpleValue: false,
    clearForms: ['null'],
    filterConditions: worldClockFilterConditions,
    filterOperators: operatorsOf(worldClockFilterConditions),
    aggregations: ['count_filled', 'count_empty', 'count', 'none'],
    emptyText: '',
    parseValue(raw) {
        return normalize(raw, false);
    },
    toText(value) {
        return value === null ? '' : worldClockText(value.timezone);
    },
    toSortKeys(value) {
        return {
            text: textShadow(value === null ? null : worldClockText(value.timezone)),
            number: null,
            date: null,
        };
    },
    isEqual(a, b) {
        if (a === null || a === undefined)
            return b === null || b === undefined;
        if (b === null || b === undefined)
            return false;
        return a.timezone === b.timezone;
    },
    toActivityValue(value) {
        return value === null ? null : { timezone: value.timezone };
    },
    fromMondayValue(raw) {
        return normalize(raw, true);
    },
    toMondayValue(value) {
        if (value === null)
            return null;
        const out = { timezone: value.timezone };
        if (value.changed_at !== undefined)
            out.changed_at = value.changed_at;
        return out;
    },
    toSimpleValue(value) {
        // Not a simple-value type, so `null` rather than `''` (M1-B5 R3).
        return value === null ? null : value.timezone;
    },
    fromMondaySettings(raw) {
        return worldClockSettingsSchema.parse(raw ?? {});
    },
    toMondaySettings(settings) {
        return { ...settings };
    },
    toMondaySettingsStr(settings) {
        return { ...settings };
    },
    toApiFragment(value) {
        if (value === null)
            return {};
        // The fragment keeps the **raw** identifier (`America/New_York`), unlike `text` `[cvs§2]`.
        return { timezone: value.timezone, updated_at: toFragmentTimestamp(value.changed_at) };
    },
    toWebhookValue(value, settings) {
        return this.toMondayValue(value, settings);
    },
    /**
     * Facet by the identifier. monday's own filter compare values are *UI display names*
     * (`["London"]`, not `Europe/London`) `[09§A.7 world_clock]` — a mapping we do not have, so
     * ours compares what it shows (q332).
     */
    bucketsOf(value) {
        return value === null ? [] : [{ key: value.timezone, label: worldClockText(value.timezone) }];
    },
    filterSql(ctx) {
        return defaultFilterSql('text', ctx);
    },
    summarySql(ctx) {
        return defaultSummarySql(ctx);
    },
});
//# sourceMappingURL=index.js.map